"use client";

import * as Sentry from "@sentry/nextjs";
import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { env } from "~/env";
import { registerSuperProperties } from "~/lib/analytics";

// Initializes wherever a project token is configured (production, staging,
// preview, or local) — point NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN at the project
// for that environment. No NODE_ENV gate, so staging flags work too. Runs once
// at module load on the client; posthog.init is idempotent regardless.
if (typeof window !== "undefined" && env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN) {
  try {
    posthog.init(env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN, {
      api_host: env.NEXT_PUBLIC_POSTHOG_HOST,
      capture_pageleave: true,
      cookieless_mode: "on_reject",
      defaults: "2026-08-30",
      save_campaign_params: true,
      custom_campaign_params: [
        "attribution_code",
        "ndclid",
        "gad_source",
        "gad_campaignid",
        "brid",
      ],
      // Default-to-cookieless on first load so ad attribution works for
      // visitors who never interact with the banner. With `cookieless_mode:
      // "on_reject"`, calling `opt_out_capturing()` registers a sentinel
      // distinct_id and starts capturing events.
      loaded: (ph) => {
        if (ph.get_explicit_consent_status() === "pending") {
          ph.opt_in_capturing();
        }
        // After the opt-in above, not before: opting in resets out of the
        // cookieless sentinel state, swapping the distinct id and the store the
        // properties persist in, so a super property registered first would not
        // reach the events that follow. See `registerSuperProperties` for the
        // other transitions that have to re-register.
        registerSuperProperties(ph);
      },
    });
  } catch {
    // PostHog failed to initialize — app continues normally
    Sentry.captureException(new Error("PostHog failed to initialize"));
  }
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  return <PHProvider client={posthog}>{children}</PHProvider>;
}
